/**
 * VerifyEmail — iter-169l. The /verify-email?token=... landing page.
 * Consumes the token, auto-signs the user in via the cookies set by
 * the backend, and routes them to /onboarding (or /dashboard if
 * already onboarded).
 *
 * Three states:
 *   • verifying  — spinner.
 *   • success    — green check + automatic redirect after 2s.
 *   • error      — friendly explanation per backend reason (expired,
 *                  invalid, network) and CTAs back to login / resend.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { useLang } from "../../contexts/LangContext";
import { useAuth } from "../../contexts/AuthContext";
import { authService } from "../../services/authService";
import { tokenStore } from "../../lib/api";
import { LumfiWordmark } from "../../components/LumfiLogo";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

export default function VerifyEmail() {
  const { lang } = useLang();
  const isEs = lang === "es";
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState(() =>
    token
      ? { status: "verifying", reason: null, email: null }
      : { status: "error", reason: "verify_invalid", email: null },
  );

  useEffect(() => {
    if (!token) return;
    let cancel = false;
    (async () => {
      try {
        const data = await authService.verifyEmail(token);
        if (cancel) return;
        if (data?.access_token) tokenStore.set(data.access_token);
        await refresh();
        setState({ status: "success", reason: null, email: data.email });
        setTimeout(() => {
          navigate(data?.onboarded ? "/dashboard" : "/onboarding");
        }, 2000);
      } catch (e) {
        if (cancel) return;
        setState({
          status: "error",
          reason: e.response?.data?.detail || "verify_invalid",
          email: null,
        });
      }
    })();
    return () => { cancel = true; };
  }, [token, navigate, refresh]);

  const reasonText = (reason) => {
    const map = {
      es: {
        verify_invalid: "El link no es válido.",
        verify_expired: "El link venció. Pide uno nuevo desde la página de verificación.",
        verify_network: "Hubo un problema verificando el link. Prueba de nuevo en un minuto.",
      },
      en: {
        verify_invalid: "This link is not valid.",
        verify_expired: "This link has expired (links last 15 minutes). Request a new one from the verification page.",
        verify_network: "Something went wrong verifying the link. Try again in a minute.",
      },
    };
    return (isEs ? map.es : map.en)[reason] || (isEs ? "Algo salió mal." : "Something went wrong.");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10 safe-bottom">
      <div className="w-full max-w-md lumfi-rise-in">
        <div className="text-center mb-7">
          <Link to="/"><LumfiWordmark /></Link>
        </div>
        <Card className="rounded-2xl border-slate-200 p-7 shadow-sm" data-testid="verify-email-card">
          {state.status === "verifying" && (
            <div className="text-center py-8 text-slate-500 flex items-center justify-center gap-2" data-testid="verify-email-loading">
              <Loader2 className="h-4 w-4 animate-spin" />
              {isEs ? "Verificando tu email…" : "Verifying your email…"}
            </div>
          )}
          {state.status === "success" && (
            <div className="text-center py-2" data-testid="verify-email-success">
              <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
              <h1 className="text-lg font-semibold text-slate-900">
                {isEs ? "¡Email confirmado!" : "Email confirmed!"}
              </h1>
              <p className="text-sm text-slate-500 mt-2">
                {isEs
                  ? "Listo. Te estamos firmando…"
                  : "All set. Signing you in…"}
              </p>
            </div>
          )}
          {state.status === "error" && (
            <div data-testid="verify-email-error">
              <div className="flex items-center gap-2 text-amber-700 mb-3">
                <AlertTriangle className="h-5 w-5" />
                <h1 className="text-lg font-semibold">
                  {isEs ? "No pudimos verificar el link" : "We couldn't verify the link"}
                </h1>
              </div>
              <p className="text-sm text-slate-600 mb-5">{reasonText(state.reason)}</p>
              <Link
                to="/verify-email-pending"
                data-testid="verify-email-pending-link"
                className="block w-full h-12 inline-flex items-center justify-center rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
              >
                {isEs ? "Pedir un nuevo link" : "Request a new link"}
              </Link>
              <div className="mt-4 text-center">
                <Link to="/login" className="text-xs text-slate-500 hover:text-slate-700">
                  {isEs ? "Volver al login" : "Back to login"}
                </Link>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
